import React from 'react';
import { FONT, TEXT3, WHITE } from '../../../shared';
import { getCssAspect } from '../../../lib/aspectRatioMatrix';

// YT_Video_16x9 — YouTube video card / thumbnail
// Reference: Vella_Preview_Mockup_Spec.md §4.12
// 16:9 thumbnail · play button overlay · duration badge · title + channel + views
export default function YouTubeThumbnailChrome({
  assetUrl,
  title = "How to grow your brand in 2026 | full walkthrough",
  channelName = "Your Brand",
  channelAvatarUrl,
  viewCount = "12K views",
  uploadedAgo = "2 days ago",
  duration = "8:42",
  verified = false,
}) {
  const ratio = getCssAspect("YouTube", "Video");

  return (
    <div style={{ fontFamily: FONT, background: "#FFFFFF", color: "#0F0F0F", width: "100%", maxWidth: 390, borderRadius: 12, overflow: "hidden", border: "1px solid #E5E5E5" }}>
      {/* Status bar */}
      <div style={{ height: 20, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 14px", fontSize: 11, fontWeight: 600 }}>
        <span>{new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
        <span style={{ letterSpacing: 1, fontSize: 10 }}>••• 5G 100</span>
      </div>

      {/* YouTube top bar */}
      <div style={{ height: 48, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 14px", borderBottom: "1px solid #F2F2F2" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 18, fontWeight: 600, letterSpacing: -0.5 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#FF0000" aria-hidden>
            <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8z" />
            <path d="M9.6 15.6V8.4l6.3 3.6-6.3 3.6z" fill="#FFFFFF" />
          </svg>
          <span>YouTube</span>
        </span>
        <div style={{ display: "flex", gap: 18, alignItems: "center", color: "#0F0F0F" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 6V4M9 7l-2-2M15 7l2-2M5 12c0 3.5 2.5 7 7 7s7-3.5 7-7H5z" /></svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
          <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#FF0000,#9B0000)", color: WHITE, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {channelName.charAt(0).toUpperCase()}
          </div>
        </div>
      </div>

      {/* Thumbnail — 16:9 */}
      <div style={{ position: "relative", width: "100%", aspectRatio: ratio, background: "#000000", overflow: "hidden" }}>
        {assetUrl
          ? <img src={assetUrl} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: TEXT3, fontSize: 12 }}>Thumbnail loading…</div>}

        {/* Center play button */}
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 56, height: 56, borderRadius: "50%", background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden>
            <path d="M8 5v14l11-7L8 5z" />
          </svg>
        </div>

        {/* Duration badge */}
        <div style={{ position: "absolute", right: 8, bottom: 8, background: "rgba(0,0,0,0.85)", color: WHITE, fontSize: 11, fontWeight: 600, padding: "2px 6px", borderRadius: 4, lineHeight: 1.2 }}>
          {duration}
        </div>
      </div>

      {/* Video meta row */}
      <div style={{ display: "flex", padding: "12px 12px 14px", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: channelAvatarUrl ? `center/cover url(${channelAvatarUrl})` : "linear-gradient(135deg,#FF0000,#9B0000)", color: WHITE, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!channelAvatarUrl && channelName.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, lineHeight: 1.35, fontWeight: 600, color: "#0F0F0F", marginBottom: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: "#606060", lineHeight: 1.4, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
            <span>{channelName}</span>
            {verified && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#606060" aria-hidden>
                <path d="M12 2l2.4 2.4 3.4-.7.6 3.4 3 1.7-1.6 3.1 1.6 3.1-3 1.7-.6 3.4-3.4-.7L12 22l-2.4-2.4-3.4.7-.6-3.4-3-1.7L4.2 12 2.6 8.9l3-1.7.6-3.4 3.4.7L12 2zm-1.2 13.4l6-6-1.4-1.4-4.6 4.6-2.4-2.4-1.4 1.4 3.8 3.8z" />
              </svg>
            )}
            <span>·</span>
            <span>{viewCount}</span>
            <span>·</span>
            <span>{uploadedAgo}</span>
          </div>
        </div>
        <span style={{ fontSize: 18, color: "#606060", lineHeight: 1, padding: "2px 4px" }}>⋮</span>
      </div>

      {/* Tab bar */}
      <div style={{ height: 48, borderTop: "1px solid #F2F2F2", display: "flex", justifyContent: "space-around", alignItems: "center", color: "#606060" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#0F0F0F" aria-hidden><path d="M3 12l9-9 9 9v9a2 2 0 01-2 2h-4v-7H10v7H6a2 2 0 01-2-2v-9z" /></svg>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M22 12c0 5.5-10 9-10 9s-10-3.5-10-9a4 4 0 017-2.6A4 4 0 0122 12z" /></svg>
        <div style={{ width: 32, height: 32, borderRadius: 16, background: "#0F0F0F", color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 300, lineHeight: 1 }}>+</div>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 11.5V18a2 2 0 01-2 2H5a2 2 0 01-2-2v-6.5l9-7 9 7z" /></svg>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M2 12h6l3 9 4-18 3 9h4" /></svg>
      </div>
    </div>
  );
}
